import { groupBy, isEmpty, lowerCase, map, uniq, upperFirst } from 'lodash';
import { Fragment, useMemo } from 'react';

import { Line } from '@/charts';
import { GenericObject, getYValue } from '@/charts/helpers';
import { LineIcon } from '@/components/data/ColorIcon';
import { Datum, unit, unitType } from '@/components/data/Datum';
import {
    DataDetailDatum,
    DataDetailsSection,
} from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';

export type LineChartDatumDetailProps<D> = {
    datum: D;
    dataset: D[];
    lines: Line<D>[];
};

const defaultDatumDetailRenderer = <D extends GenericObject>(
    datum: D,
    { label, color, valueKey, ...units }: Line<D>
) => (
    <DataDetailDatum label={label} icon={<LineIcon dot color={color.line} />}>
        <Datum value={getYValue(datum, valueKey)} type={unitType(units, datum)} sourceUnit={unit(units, datum)} />
    </DataDetailDatum>
);

export const LineChartDatumDetail = <D extends GenericObject>({
    datum,
    lines,
    dataset,
}: LineChartDatumDetailProps<D>) => {
    const sensorPositionLabel = useMemo(
        () =>
            datum.sensorPosition && uniq(map(dataset, 'sensorPosition')).length > 1
                ? upperFirst(lowerCase(String(datum.sensorPosition)))
                : null,
        [dataset, datum.sensorPosition]
    );

    const linesBySensorLabel = useMemo(() => {
        const linesForDatum = lines.filter(
            ({ valueKey, dataFilter, renderMissingValueDot }) =>
                (!dataFilter || dataFilter(datum)) && (renderMissingValueDot || Number.isFinite(datum[valueKey]))
        );

        return groupBy(linesForDatum, ({ sensorLabel }) => sensorLabel ?? sensorPositionLabel ?? '');
    }, [datum, lines, sensorPositionLabel]);

    if (isEmpty(linesBySensorLabel)) return false;

    return map(linesBySensorLabel, (linesForDatum, sensorLabel) => (
        <DataDetailsSection label={sensorLabel} key={sensorLabel}>
            {linesForDatum.map((line) => {
                const datumDetailRenderer = line.datumDetailRenderer ?? defaultDatumDetailRenderer;
                return <Fragment key={line.id}>{datumDetailRenderer(datum, line)}</Fragment>;
            })}
        </DataDetailsSection>
    ));
};
